import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, GraduationCap, Shield } from "lucide-react";
import graduationCelebration from "@/assets/graduation-celebration.jpg";

export function Certifications() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img src={graduationCelebration} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/90" />
      </div>

      <div className="container px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center mb-20"
        >
          <p className="text-sm md:text-base font-bold uppercase tracking-[0.25em] text-accent mb-6">
            Soul Safe Sanctuary™ Trainings
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl font-black text-foreground mb-6 leading-[1.05] tracking-[-0.02em]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            We Listen.
            <br />
            <span className="md:whitespace-nowrap">We Educate. We Certify.</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-px bg-border max-w-4xl mx-auto">
          {/* Survivor Certification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-card p-10"
          >
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-6">
              <GraduationCap className="w-6 h-6 text-accent" />
            </div>
            <p className="text-xs font-semibold text-accent uppercase tracking-wider mb-4">
              Survivor Certification
            </p>
            <h3 className="font-serif text-2xl font-semibold text-foreground mb-4">
              Soul Safe Sanctuary™ Graduate
            </h3>
            <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
              A Christ-centered recovery journey focused on understanding abuse,
              identifying manipulation, rebuilding identity, and developing healthy boundaries.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Open-access and completion-based",
                "Never expires",
                "Honors growth and resilience",
                "No pressure or performance",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="w-1 h-1 rounded-full bg-accent mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground/70 italic">
              For survivors who want language, understanding, and healing.
            </p>
          </motion.div>

          {/* Leader Certification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-card p-10"
          >
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-6">
              <Shield className="w-6 h-6 text-accent" />
            </div>
            <p className="text-xs font-semibold text-accent uppercase tracking-wider mb-4">
              Leader Certification
            </p>
            <h3 className="font-serif text-2xl font-semibold text-foreground mb-4">
              Trained Soul Safe Sanctuary™ Leader
            </h3>
            <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
              Affirms a leader's commitment to trauma-informed discipleship,
              godly authority, transparent leadership, and safety-centered governance.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Faith-based and educational",
                "Includes self-assessment",
                "Emphasizes responsibility, not power",
                "Aligned with SoulSafe values",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="w-1 h-1 rounded-full bg-accent mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground/70 italic">
              For leaders committed to safety in the Kingdom.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-muted-foreground mb-8 max-w-xl mx-auto">
            Our certifications are educational and faith-based. They provide shared language
            and ethical grounding, not clinical licensure. Safety is not optional in the Kingdom.
          </p>
          <Button asChild className="h-14 px-8 text-base font-semibold rounded-full">
            <Link to="/auth">
              Begin Your Certification Path
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
